import React, { useState, useEffect } from 'react';
import { CredentialUploader } from './CredentialUploader';
import { CredentialDebugger } from './CredentialDebugger';

interface Step {
  id: string;
  title: string;
  description: string;
  completed: boolean;
}

export const IAMPermissionPalace: React.FC = () => {
  const [currentStep, setCurrentStep] = useState(0);
  const [credentials, setCredentials] = useState({
    accessKeyId: '',
    secretAccessKey: '',
    userName: '',
    arn: ''
  });
  const [showPolicy, setShowPolicy] = useState(false);
  const [copiedItem, setCopiedItem] = useState<string | null>(null);
  const [validationStatus, setValidationStatus] = useState<'idle' | 'validating' | 'valid' | 'invalid'>('idle');
  const [savedCredentials, setSavedCredentials] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [saveSteps, setSaveSteps] = useState<string[]>([]);

  const steps: Step[] = [
    {
      id: 'login',
      title: 'Login to AWS Console',
      description: 'Sign in with your root account',
      completed: currentStep > 0
    },
    {
      id: 'navigate-iam',
      title: 'Navigate to IAM',
      description: 'Go to Identity and Access Management',
      completed: currentStep > 1
    },
    {
      id: 'create-user',
      title: 'Create New IAM User',
      description: 'Create a user for programmatic access',
      completed: currentStep > 2
    },
    {
      id: 'attach-policy',
      title: 'Attach SES Policy',
      description: 'Add permissions for email sending',
      completed: currentStep > 3
    },
    {
      id: 'create-keys',
      title: 'Generate Access Keys',
      description: 'Create programmatic access credentials',
      completed: currentStep > 4
    },
    {
      id: 'save-credentials',
      title: 'Save & Test Credentials',
      description: 'Store and validate your keys',
      completed: currentStep > 5
    }
  ];

  const sesPolicy = {
    Version: "2012-10-17",
    Statement: [
      {
        Effect: "Allow",
        Action: [
          "ses:SendEmail",
          "ses:SendRawEmail",
          "ses:SendTemplatedEmail",
          "ses:SendBounce",
          "ses:VerifyDomainIdentity",
          "ses:VerifyEmailIdentity",
          "ses:VerifyDomainDkim",
          "ses:VerifyDomainIdentity",
          "ses:VerifyEmailAddress",
          "ses:GetIdentityVerificationAttributes",
          "ses:GetIdentityDkimAttributes",
          "ses:PutIdentityPolicy",
          "ses:GetSendQuota",
          "ses:GetSendStatistics",
          "ses:ListIdentities",
          "ses:ListVerifiedEmailAddresses",
          "ses:GetAccountSendingEnabled",
          "ses:DescribeConfigurationSet",
          "ses:PutConfigurationSetEventDestination"
        ],
        Resource: "*"
      }
    ]
  };

  const copyToClipboard = (text: string, itemId: string) => {
    navigator.clipboard.writeText(text);
    setCopiedItem(itemId);
    setTimeout(() => setCopiedItem(null), 2000);
  };

  // Fetch saved credentials on mount
  useEffect(() => {
    fetchSavedCredentials();
  }, []);

  const fetchSavedCredentials = async () => {
    try {
      const response = await fetch('http://localhost:8787/api/aws/credentials');
      const data = await response.json();
      if (data.success && data.credentials) {
        setSavedCredentials(data.credentials);
      }
    } catch (error) {
      console.error('Failed to fetch credentials:', error);
    }
  };

  const validateCredentials = async () => {
    setValidationStatus('validating');
    setSaveSteps([]);

    // Log every fucking step
    const addStep = (step: string) => {
      setSaveSteps(prev => [...prev, `${new Date().toLocaleTimeString()}: ${step}`]);
    };

    addStep('🔍 Starting validation...');
    addStep(`📝 Access Key: ${credentials.accessKeyId}`);
    addStep(`📝 Secret Key: ${credentials.secretAccessKey.substring(0, 10)}...`);
    addStep(`📏 Access Key Length: ${credentials.accessKeyId.length}/20`);
    addStep(`📏 Secret Key Length: ${credentials.secretAccessKey.length}/40`);

    // AWS Access Key format: 20 characters, can be uppercase OR lowercase letters and numbers
    // Real AWS keys can actually have mixed case!
    const isValidAccessKey = /^[A-Za-z0-9]{20}$/.test(credentials.accessKeyId);

    // AWS Secret Key format: 40 characters, base64 characters (letters, numbers, +, /, =)
    // Include = for padding in base64
    const isValidSecretKey = /^[A-Za-z0-9+/=]{40}$/.test(credentials.secretAccessKey);

    addStep(`✅ Access Key Valid: ${isValidAccessKey}`);
    addStep(`✅ Secret Key Valid: ${isValidSecretKey}`);

    if (!isValidAccessKey) {
      addStep('❌ FAILED: Invalid access key format');
      setValidationStatus('invalid');
      return;
    }

    if (!isValidSecretKey) {
      addStep('❌ FAILED: Invalid secret key format');
      setValidationStatus('invalid');
      return;
    }

    addStep('✅ Format validation passed!');
    addStep('📤 Preparing to send to D1 database...');

    // Save to D1 database
    try {
      const payload = {
        accessKeyId: credentials.accessKeyId,
        secretAccessKey: credentials.secretAccessKey,
        region: 'us-east-1',
        userName: credentials.userName || 'ses-email-sender',
        userArn: credentials.arn,
        credentialType: 'iam',
        userId: 1
      };

      addStep(`📦 Payload prepared: ${JSON.stringify(payload).substring(0, 100)}...`);
      addStep('🚀 Sending POST request to /api/aws/credentials...');

      const response = await fetch('http://localhost:8787/api/aws/credentials', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload)
      });

      addStep(`📡 Response status: ${response.status} ${response.statusText}`);

      const data = await response.json();
      addStep(`📥 Response data: ${JSON.stringify(data)}`);

      if (data.success) {
        addStep(`✅ SUCCESS! Saved with ID: ${data.credentialId}`);
        addStep('🔄 Refreshing credential list...');

        setValidationStatus('valid');

        // Refresh saved credentials list
        await fetchSavedCredentials();
        addStep('✅ Credential list refreshed');
        addStep(`📊 Total credentials now: ${savedCredentials.length + 1}`);

        setTimeout(() => {
          setCurrentStep(6);
        }, 1500);
      } else {
        addStep(`❌ SAVE FAILED: ${data.error || 'Unknown error'}`);
        setValidationStatus('invalid');
      }
    } catch (error: any) {
      addStep(`❌ EXCEPTION: ${error.message}`);
      setValidationStatus('invalid');
    }
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 0: // Login
        return (
          <div className="space-y-4">
            <div className="bg-blue-900/20 border border-blue-500 rounded-lg p-4">
              <h3 className="font-bold text-blue-400 mb-2">🔑 AWS Console Access</h3>
              <p className="mb-3">You'll need to login to your AWS account to create an IAM user.</p>
              <a
                href="https://console.aws.amazon.com/iam"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-block bg-blue-600 px-4 py-2 rounded font-bold hover:bg-blue-700"
              >
                Open AWS IAM Console →
              </a>
            </div>

            <div className="bg-yellow-900/20 border border-yellow-500 rounded-lg p-4">
              <h3 className="font-bold text-yellow-400 mb-2">⚠️ Important</h3>
              <ul className="space-y-1 text-sm">
                <li>• Use your ROOT account to create the IAM user</li>
                <li>• After this, you'll use IAM credentials only</li>
                <li>• Never share or commit your credentials</li>
              </ul>
            </div>

            <button
              onClick={() => setCurrentStep(1)}
              className="w-full bg-green-600 py-2 rounded font-bold hover:bg-green-700"
            >
              I'm Logged In - Continue →
            </button>
          </div>
        );

      case 1: // Navigate to IAM
        return (
          <div className="space-y-4">
            <div className="bg-gray-800 rounded-lg p-4">
              <h3 className="font-bold mb-3">📍 Navigation Steps:</h3>
              <ol className="space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <span className="bg-blue-600 px-2 py-1 rounded text-xs">1</span>
                  <span>In AWS Console, search for "IAM" in the top search bar</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="bg-blue-600 px-2 py-1 rounded text-xs">2</span>
                  <span>Click on "IAM" (Identity and Access Management)</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="bg-blue-600 px-2 py-1 rounded text-xs">3</span>
                  <span>In the left sidebar, click on "Users"</span>
                </li>
              </ol>
            </div>

            <button
              onClick={() => setCurrentStep(2)}
              className="w-full bg-green-600 py-2 rounded font-bold hover:bg-green-700"
            >
              I'm in IAM Users Section →
            </button>
          </div>
        );

      case 2: // Create User
        return (
          <div className="space-y-4">
            <div className="bg-gray-800 rounded-lg p-4">
              <h3 className="font-bold mb-3">👤 Create IAM User:</h3>
              <ol className="space-y-3 text-sm">
                <li className="flex items-start gap-2">
                  <span className="bg-blue-600 px-2 py-1 rounded text-xs">1</span>
                  <div>
                    <p>Click "Add users" button</p>
                  </div>
                </li>
                <li className="flex items-start gap-2">
                  <span className="bg-blue-600 px-2 py-1 rounded text-xs">2</span>
                  <div>
                    <p>User name: <code className="bg-black/50 px-2 py-1 rounded">ses-email-sender</code></p>
                    <button
                      onClick={() => copyToClipboard('ses-email-sender', 'username')}
                      className="text-xs text-blue-400 hover:text-blue-300 mt-1"
                    >
                      {copiedItem === 'username' ? '✅ Copied' : '📋 Copy'}
                    </button>
                  </div>
                </li>
                <li className="flex items-start gap-2">
                  <span className="bg-blue-600 px-2 py-1 rounded text-xs">3</span>
                  <div>
                    <p>Select AWS credential type:</p>
                    <p className="text-yellow-400 font-bold">✅ Access key - Programmatic access</p>
                  </div>
                </li>
                <li className="flex items-start gap-2">
                  <span className="bg-blue-600 px-2 py-1 rounded text-xs">4</span>
                  <span>Click "Next: Permissions"</span>
                </li>
              </ol>
            </div>

            <button
              onClick={() => setCurrentStep(3)}
              className="w-full bg-green-600 py-2 rounded font-bold hover:bg-green-700"
            >
              User Created - Add Permissions →
            </button>
          </div>
        );

      case 3: // Attach Policy
        return (
          <div className="space-y-4">
            <div className="bg-gray-800 rounded-lg p-4">
              <h3 className="font-bold mb-3">📋 Add SES Permissions:</h3>

              <div className="mb-4 p-3 bg-blue-900/30 rounded">
                <p className="text-sm mb-2">Option 1: Use AWS Managed Policy (Easier)</p>
                <p className="font-mono text-xs bg-black/50 p-2 rounded">AmazonSESFullAccess</p>
              </div>

              <div className="mb-4">
                <p className="text-sm mb-2">Option 2: Create Custom Policy (Recommended)</p>
                <button
                  onClick={() => setShowPolicy(!showPolicy)}
                  className="bg-gray-700 px-3 py-1 rounded text-sm hover:bg-gray-600"
                >
                  {showPolicy ? 'Hide' : 'Show'} Custom Policy
                </button>
              </div>

              {showPolicy && (
                <div className="relative">
                  <pre className="bg-black/50 p-3 rounded text-xs overflow-x-auto max-h-64 overflow-y-auto">
                    {JSON.stringify(sesPolicy, null, 2)}
                  </pre>
                  <button
                    onClick={() => copyToClipboard(JSON.stringify(sesPolicy, null, 2), 'policy')}
                    className="absolute top-2 right-2 bg-gray-700 px-2 py-1 rounded text-xs hover:bg-gray-600"
                  >
                    {copiedItem === 'policy' ? '✅ Copied' : '📋 Copy Policy'}
                  </button>
                </div>
              )}

              <ol className="space-y-2 text-sm mt-4">
                <li>1. Choose "Attach existing policies directly"</li>
                <li>2. Search for "SES"</li>
                <li>3. Check ✅ AmazonSESFullAccess</li>
                <li>4. Click "Next: Tags" (skip tags)</li>
                <li>5. Click "Next: Review"</li>
                <li>6. Click "Create user"</li>
              </ol>
            </div>

            <button
              onClick={() => setCurrentStep(4)}
              className="w-full bg-green-600 py-2 rounded font-bold hover:bg-green-700"
            >
              Policy Attached - Generate Keys →
            </button>
          </div>
        );

      case 4: // Create Access Keys
        return (
          <div className="space-y-4">
            <div className="bg-red-900/20 border border-red-500 rounded-lg p-4">
              <h3 className="font-bold text-red-400 mb-2">🔴 Critical Step - One Time Only!</h3>
              <p className="text-sm">You can only view the secret access key once. Save it immediately!</p>
            </div>

            <div className="bg-gray-800 rounded-lg p-4">
              <h3 className="font-bold mb-3">🔑 Generate Access Keys:</h3>
              <ol className="space-y-3 text-sm">
                <li>1. You should see "Success" message with user created</li>
                <li>2. Click on the username <code>ses-email-sender</code></li>
                <li>3. Go to "Security credentials" tab</li>
                <li>4. Under "Access keys", click "Create access key"</li>
                <li>5. Select "Application running outside AWS"</li>
                <li>6. Click "Next" and then "Create access key"</li>
                <li className="font-bold text-yellow-400">7. ⚠️ COPY BOTH KEYS NOW - You won't see the secret again!</li>
              </ol>
            </div>

            <div className="bg-gray-900 rounded-lg p-4">
              <p className="text-sm mb-2">Your credentials will look like:</p>
              <div className="space-y-2 text-xs">
                <p>Access Key ID: <code>AKIAIOSFODNN7EXAMPLE</code> (20 chars)</p>
                <p>Secret Access Key: <code>wJalrXUtnFEMI/K7MDENG/bPxRfiCYEXAMPLEKEY</code> (40 chars)</p>
              </div>
            </div>

            <button
              onClick={() => setCurrentStep(5)}
              className="w-full bg-green-600 py-2 rounded font-bold hover:bg-green-700"
            >
              I Have My Keys - Save Them →
            </button>
          </div>
        );

      case 5: // Save Credentials
        return (
          <div className="space-y-4">
            {/* CSV Upload Option */}
            <CredentialUploader
              onCredentialsLoaded={async (csvCreds) => {
                // Set form fields from CSV
                setCredentials({
                  userName: csvCreds.userName || '',
                  accessKeyId: csvCreds.accessKeyId,
                  secretAccessKey: csvCreds.secretAccessKey,
                  arn: csvCreds.userArn || ''
                });
                // Auto-save after loading from CSV
                setTimeout(() => validateCredentials(), 500);
              }}
            />

            <div className="flex items-center my-4">
              <hr className="flex-1 border-gray-700" />
              <span className="px-4 text-gray-500 text-sm">OR Enter Manually</span>
              <hr className="flex-1 border-gray-700" />
            </div>

            <div className="bg-gray-800 rounded-lg p-4">
              <h3 className="font-bold mb-3">💾 Save Your Credentials:</h3>

              <div className="space-y-3">
                <div>
                  <label className="block text-sm mb-1">IAM Username</label>
                  <input
                    type="text"
                    value={credentials.userName}
                    onChange={(e) => setCredentials({...credentials, userName: e.target.value})}
                    placeholder="ses-email-sender"
                    className="w-full px-3 py-2 bg-black/50 rounded border border-gray-700 text-white"
                  />
                </div>

                <div>
                  <label className="block text-sm mb-1">Access Key ID</label>
                  <input
                    type="text"
                    value={credentials.accessKeyId}
                    onChange={(e) => setCredentials({...credentials, accessKeyId: e.target.value.toUpperCase()})}
                    placeholder="AKIAIOSFODNN7EXAMPLE"
                    maxLength={20}
                    className="w-full px-3 py-2 bg-black/50 rounded border border-gray-700 text-white font-mono"
                  />
                  <p className="text-xs text-gray-500 mt-1">20 characters, uppercase letters and numbers</p>
                </div>

                <div>
                  <label className="block text-sm mb-1">Secret Access Key</label>
                  <input
                    type="password"
                    value={credentials.secretAccessKey}
                    onChange={(e) => setCredentials({...credentials, secretAccessKey: e.target.value})}
                    placeholder="••••••••••••••••••••••••••••••••••••••••"
                    maxLength={40}
                    className="w-full px-3 py-2 bg-black/50 rounded border border-gray-700 text-white font-mono"
                  />
                  <p className="text-xs text-gray-500 mt-1">40 characters, mixed case and special characters</p>
                </div>

                <div>
                  <label className="block text-sm mb-1">User ARN (Optional)</label>
                  <input
                    type="text"
                    value={credentials.arn}
                    onChange={(e) => setCredentials({...credentials, arn: e.target.value})}
                    placeholder="arn:aws:iam::123456789012:user/ses-email-sender"
                    className="w-full px-3 py-2 bg-black/50 rounded border border-gray-700 text-white font-mono text-sm"
                  />
                </div>
              </div>

              {validationStatus === 'invalid' && (
                <div className="mt-3 p-2 bg-red-900/30 border border-red-600 rounded">
                  <p className="text-sm text-red-400 font-bold mb-2">❌ VALIDATION FAILED - HERE'S EXACTLY WHAT'S WRONG:</p>

                  <div className="space-y-2 text-xs">
                    <div className="bg-black/50 p-2 rounded border border-red-800">
                      <p className="text-red-300 font-bold">Access Key:</p>
                      <p className="font-mono text-white break-all">{credentials.accessKeyId || '(empty)'}</p>
                      <p className="text-red-400 mt-1">
                        Length: {credentials.accessKeyId.length}/20
                        {credentials.accessKeyId.length !== 20 && ' ❌ WRONG LENGTH!'}
                      </p>
                      {!/^[A-Za-z0-9]*$/.test(credentials.accessKeyId) && (
                        <p className="text-red-400">❌ Contains invalid characters (only A-Z, a-z, and 0-9 allowed)</p>
                      )}
                    </div>

                    <div className="bg-black/50 p-2 rounded border border-red-800">
                      <p className="text-red-300 font-bold">Secret Key:</p>
                      <p className="font-mono text-white break-all">{credentials.secretAccessKey.substring(0, 10)}...{credentials.secretAccessKey.slice(-10)}</p>
                      <p className="text-red-400 mt-1">
                        Length: {credentials.secretAccessKey.length}/40
                        {credentials.secretAccessKey.length !== 40 && ' ❌ WRONG LENGTH!'}
                      </p>
                      {!/^[A-Za-z0-9+/=]*$/.test(credentials.secretAccessKey) && (
                        <p className="text-red-400">❌ Contains invalid characters (only A-Z, a-z, 0-9, +, /, = allowed)</p>
                      )}
                    </div>
                  </div>

                  <p className="text-xs text-gray-400 mt-2">Check browser console for full details</p>
                </div>
              )}

              {validationStatus === 'valid' && (
                <div className="mt-3 p-2 bg-green-900/30 border border-green-600 rounded">
                  <p className="text-sm text-green-400">✅ Credentials validated and saved to database!</p>
                </div>
              )}

              {/* SHOW EVERY FUCKING STEP */}
              {saveSteps.length > 0 && (
                <div className="mt-4 p-3 bg-black border border-cyan-500 rounded">
                  <h4 className="text-sm font-bold mb-2 text-cyan-400">📋 SAVE PROCESS LOG:</h4>
                  <div className="space-y-1 max-h-48 overflow-y-auto">
                    {saveSteps.map((step, idx) => (
                      <div key={idx} className="text-xs font-mono text-gray-300">
                        {step}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Show saved credentials */}
              {savedCredentials.length > 0 && (
                <div className="mt-4 p-3 bg-gray-800 rounded">
                  <h4 className="text-sm font-bold mb-2 text-gray-400">📁 Saved Credentials in D1 ({savedCredentials.length}):</h4>
                  <div className="space-y-2">
                    {savedCredentials.map((cred: any) => (
                      <div key={cred.id} className="text-xs bg-black/30 p-2 rounded border border-gray-700">
                        <div className="flex justify-between items-center">
                          <span className="font-mono">{cred.user_name || 'IAM User'}</span>
                          <span className="text-gray-500">{new Date(cred.created_at).toLocaleDateString()}</span>
                        </div>
                        <div className="text-gray-500 mt-1">
                          Key: {cred.access_key_id.substring(0, 4)}...{cred.access_key_id.slice(-4)}
                        </div>
                        <div className="text-green-400 text-xs mt-1">
                          ✓ Active in Database
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <button
                onClick={validateCredentials}
                disabled={!credentials.accessKeyId || !credentials.secretAccessKey || validationStatus === 'validating'}
                className="w-full mt-4 bg-blue-600 py-2 rounded font-bold hover:bg-blue-700 disabled:opacity-50"
              >
                {validationStatus === 'validating' ? 'Validating...' : 'Validate & Save Credentials'}
              </button>
            </div>

            <div className="bg-yellow-900/20 border border-yellow-600 rounded-lg p-4">
              <h3 className="font-bold text-yellow-400 mb-2">🔒 Security Best Practices:</h3>
              <ul className="space-y-1 text-sm">
                <li>• Never commit credentials to Git</li>
                <li>• Use environment variables in production</li>
                <li>• Rotate keys every 90 days</li>
                <li>• Enable MFA on your IAM user</li>
              </ul>
            </div>
          </div>
        );

      case 6: // Success
        return (
          <div className="space-y-4">
            <div className="text-center py-8">
              <div className="text-6xl mb-4">🎉</div>
              <h2 className="text-2xl font-bold mb-2">IAM User Created!</h2>
              <p className="text-gray-400">Your AWS credentials are ready for SES</p>
            </div>

            <div className="bg-green-900/20 border border-green-600 rounded-lg p-4">
              <h3 className="font-bold text-green-400 mb-2">✅ What You've Accomplished:</h3>
              <ul className="space-y-1 text-sm">
                <li>• Created IAM user: {credentials.userName || 'ses-email-sender'}</li>
                <li>• Attached SES permissions</li>
                <li>• Generated programmatic access keys</li>
                <li>• Validated credentials format</li>
              </ul>
            </div>

            <div className="bg-blue-900/20 border border-blue-600 rounded-lg p-4">
              <h3 className="font-bold text-blue-400 mb-2">🚀 Next Steps:</h3>
              <ol className="space-y-1 text-sm">
                <li>1. Verify your sending domain</li>
                <li>2. Request production access (escape sandbox)</li>
                <li>3. Set up bounce and complaint handling</li>
                <li>4. Start sending emails!</li>
              </ol>
            </div>

            <button
              onClick={() => window.location.href = '/domains'}
              className="w-full bg-green-600 py-3 rounded font-bold hover:bg-green-700 text-lg"
            >
              Continue to Domain Setup →
            </button>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-gray-900 text-white p-8">
      {/* CREDENTIAL DEBUGGER - Shows EXACTLY what's in D1 */}
      <CredentialDebugger />

      <div className="max-w-3xl mx-auto">
        {/* Header */}
        <div className="mb-8 text-center">
          <h1 className="text-3xl font-bold mb-2">🏰 IAM Permission Palace</h1>
          <p className="text-gray-400">Create an IAM user with SES permissions</p>
        </div>

        {/* Progress Steps */}
        <div className="mb-8">
          <div className="flex justify-between items-center">
            {steps.map((step, index) => (
              <div key={step.id} className="flex items-center">
                <div className="flex flex-col items-center">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold border-2 ${
                    step.completed ? 'bg-green-600 border-green-500' :
                    index === currentStep ? 'bg-blue-600 border-blue-500' :
                    'bg-gray-800 border-gray-700'
                  }`}>
                    {step.completed ? '✓' : index + 1}
                  </div>
                  <span className="text-xs mt-2 text-center max-w-[80px]">
                    {step.title}
                  </span>
                </div>
                {index < steps.length - 1 && (
                  <div className={`w-full h-0.5 mx-2 ${
                    step.completed ? 'bg-green-500' : 'bg-gray-700'
                  }`} />
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Main Content */}
        <div className="bg-gray-800 rounded-lg p-6">
          <h2 className="text-xl font-bold mb-4">{steps[currentStep]?.title}</h2>
          {renderStepContent()}
        </div>
      </div>
    </div>
  );
};